import { loadMusicXML, makeMxl, unrollMusicXML } from '../../../packages/core/src/index.js';

type Payload = {
  filename: string;
  sourceName: string;
  compressed: boolean;
  base: string;
  musicxml: string;
  midi: ArrayBuffer;
  mxl: ArrayBuffer;
  report: any;
  input: { partCount: number; printedMeasureCount: number };
  plan: { steps: Array<{ sourceIndex: number; repeatPass: number }>; endingSpans: Array<{ numbers: Set<number> }> };
};

const $ = <T extends HTMLElement>(selector: string) => document.querySelector(selector) as T;
const drop = $<HTMLDivElement>('#drop');
const input = $<HTMLInputElement>('#file');
const status = $<HTMLDivElement>('#status');
const result = $<HTMLDivElement>('#result');
const meta = $<HTMLDivElement>('#meta');
const route = $<HTMLDivElement>('#route');
const tempo = $<HTMLDivElement>('#tempo');
let current: Payload | null = null;

async function run(file: File): Promise<void> {
  status.className = 'status busy';
  status.textContent = `Processing ${file.name}…`;
  result.hidden = true;

  // Let the status paint before doing the synchronous semantic transform.
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const loaded = await loadMusicXML(bytes, file.name);
    const converted = unrollMusicXML(loaded.xml, file.name);
    const base = (loaded.sourceName.replace(/\.(musicxml|xml|mxl)$/i, '') || 'score') + '-unrolled';
    const mxl = makeMxl(converted.musicxml, `${base}.musicxml`);

    show({
      filename: file.name,
      sourceName: loaded.sourceName,
      compressed: loaded.compressed,
      base,
      musicxml: converted.musicxml,
      midi: converted.midi.buffer,
      mxl: mxl.buffer,
      report: converted.report,
      input: converted.input,
      plan: converted.plan,
    });
  } catch (error) {
    status.className = 'status error';
    status.textContent = error instanceof Error ? error.message : String(error);
  }
}

function show(payload: Payload): void {
  current = payload;
  status.className = 'status ok';
  status.textContent = '✓ Timing + tempo map preserved · navigation flattened · lyrics resolved';
  const timing = Object.values(payload.report.timingQuarters)[0] as { unrolledOutput?: string } | undefined;
  const tempoMap = payload.report.tempoMap as Array<{ quarter: string; bpm: string }>;
  meta.innerHTML = `<div><b>${payload.input.partCount}</b><span>parts</span></div><div><b>${payload.input.printedMeasureCount}</b><span>printed measures</span></div><div><b>${payload.report.expandedMeasureCount}</b><span>linear measures</span></div><div><b>${timing?.unrolledOutput ?? '—'}</b><span>quarter notes</span></div><div><b>${tempoMap.length}</b><span>tempo events</span></div><div><b>${Number(payload.report.playbackSeconds).toFixed(3)}</b><span>playback seconds</span></div>`;
  route.textContent = compressRoute(payload.report.sourceOrderZeroBased as number[]);
  tempo.textContent = tempoMap.map(e => `${e.quarter}q @ ${e.bpm} BPM`).join('  →  ');
  result.hidden = false;
}

function compressRoute(order: number[]): string {
  if (!order.length) return '';
  const groups: string[] = [];
  let start = order[0]!;
  let previous = start;
  for (let i = 1; i <= order.length; i++) {
    const currentIndex = order[i];
    if (currentIndex === previous + 1) {
      previous = currentIndex;
      continue;
    }
    groups.push(start === previous ? `${start + 1}` : `${start + 1}–${previous + 1}`);
    if (currentIndex != null) {
      start = currentIndex;
      previous = currentIndex;
    }
  }
  return groups.join(' → ');
}

function download(data: BlobPart, name: string, type: string): void {
  const anchor = document.createElement('a');
  anchor.href = URL.createObjectURL(new Blob([data], { type }));
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(anchor.href), 1000);
}

$('#xml').onclick = () => current && download(current.musicxml, `${current.base}.musicxml`, 'application/vnd.recordare.musicxml+xml');
$('#mxl').onclick = () => current && download(current.mxl, `${current.base}.mxl`, 'application/vnd.recordare.musicxml');
$('#midi').onclick = () => current && download(current.midi, `${current.base}.mid`, 'audio/midi');
$('#json').onclick = () => current && download(JSON.stringify(current.report, null, 2) + '\n', `${current.base}.json`, 'application/json');

input.onchange = () => input.files?.[0] && void run(input.files[0]);
drop.onclick = () => input.click();
drop.onkeydown = event => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    input.click();
  }
};
drop.ondragover = event => {
  event.preventDefault();
  drop.classList.add('over');
};
drop.ondragleave = () => drop.classList.remove('over');
drop.ondrop = event => {
  event.preventDefault();
  drop.classList.remove('over');
  const file = event.dataTransfer?.files[0];
  if (file) void run(file);
};
